import { createContext, useContext, useEffect, useRef, useState } from "react";

const worker = new SharedWorker(
  new URL("../workers/shared.worker", import.meta.url),
  { type: "module" }
);
worker.port.start();

const broadcastChannel = new BroadcastChannel("rpc");

export type TCallRpcMethod = (method: string, params?: any) => Promise<any>;

export interface IRpcContext {
  callRpcMethod: TCallRpcMethod;
  subscribe: (event: string, callback: (data: any) => void) => void;
  unsubscribe: (event: string, callback: (data: any) => void) => void;
  wsOpened: boolean;
  connect: () => void;
}

const RpcContext = createContext<IRpcContext | null>(null);

export const RpcProvider = ({ children }: { children: React.ReactNode }) => {
  const [wsOpened, setWsOpened] = useState(false);
  const activeSubscriptions = useRef(new Map<string, (data: any) => void>());
  const addTokenToParams = (object: any) => {
    if (object) {
      const token = localStorage.getItem("authToken");
      if (token) {
        object["token"] = token;
      }

      return object;
    }

    return object;
  };

  // Функция вызова JSON-RPC метода
  const callRpcMethod = (method: string, params: any) => {
    const id = Math.random().toString(36).substr(2, 9);
    return new Promise((resolve, reject) => {
      const handleMessage = (event: any) => {
        const data = event.data;

        if (data.type === "response" && data.id === id) {
          broadcastChannel.removeEventListener("message", handleMessage);
          if (data.error) {
            reject(data.error);
          } else if (
            //не удалась подписка lock_unlock
            method === "subscribe" &&
            params === "lock_unlock" &&
            data.result === false
          ) {
            reject(new Error("Subscription failed: result is false"));
          } else {
            resolve(data.result);
          }
        }
      };

      broadcastChannel.addEventListener("message", handleMessage);
      // Отправляем запрос воркеру
      broadcastChannel.postMessage({
        type: "call",
        method,
        params: typeof params === "string" ? params : addTokenToParams(params),
        id,
      });
    });
  };

  // Функция подписки на серверные события
  const subscribe = (eventName: string, callback: (data: any) => void) => {
    activeSubscriptions.current.set(eventName, callback);
    broadcastChannel.postMessage({ type: "subscribe", event: eventName });
  };

  const unsubscribe = (eventName: string, callback: (data: any) => void) => {
    activeSubscriptions.current.delete(eventName);
    broadcastChannel.postMessage({ type: "unsubscribe", event: eventName });
  };

  const connect = () => {
    broadcastChannel.postMessage({
      type: "connect",
    });
  };

  const socketStateHandler = (msgEvent: any) => {
    const data = msgEvent.data;

    if (data.type !== "socketStatus") return;
    switch (data.status) {
      case "open":
        setWsOpened(true);
        break;
      case "close":
        setWsOpened(false);
        break;
      case "client:dumped":
        setWsOpened(false);
        localStorage.removeItem("authToken");
        break;
      case "error":
        setWsOpened(false);
    }
  };

  useEffect(() => {
    broadcastChannel.addEventListener("message", socketStateHandler);

    return () => {
      broadcastChannel.removeEventListener("message", socketStateHandler);
    };
  }, []);

  const handler = (msgEvent: any) => {
    const data = msgEvent.data;
    if (data.type === "event") {
      const targetSubscription = activeSubscriptions.current.get(data.event);
      if (targetSubscription && data.data) {
        targetSubscription(data.data);
      }
    }
  };

  useEffect(() => {
    broadcastChannel.addEventListener("message", handler);

    return () => {
      broadcastChannel.removeEventListener("message", handler);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("load", () => {
      connect();
    });

    return () => {
      window.removeEventListener("load", () => {
        connect();
      });
    };
  }, []);

  return (
    <RpcContext.Provider
      value={{ callRpcMethod, subscribe, unsubscribe, wsOpened, connect }}
    >
      {children}
    </RpcContext.Provider>
  );
};

export const useRpc = (): IRpcContext => {
  const context = useContext(RpcContext);
  if (!context) {
    throw new Error("useRpc must be used within a RpcProvider");
  }
  return context;
};
